// components/SharedSpacePanel.tsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
} from '@mui/material';
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import HomeIcon from "@mui/icons-material/Home";
import { CustomerAddress } from "../types/address";
import { ValidationErrors } from '../types/customer';
import { colors, borderRadius, typography, components, elevation } from '../theme';

interface SharedSpacePanelProps {
  addresses: CustomerAddress[];
  validationErrors: ValidationErrors;
  disabled?: boolean;
  onAddCareHome: () => void;
  onEditAddress: (address: CustomerAddress) => void;
  onRemoveAddress: (index: number) => void;
  onUpdateAddress: (index: number, updates: Partial<CustomerAddress>) => void;
  onCareHomeClick?: () => void;
}

export const SharedSpacePanel: React.FC<SharedSpacePanelProps> = ({
  addresses,
  validationErrors,
  disabled = false,
  onAddCareHome,
  onEditAddress,
  onRemoveAddress,
  onUpdateAddress,
  onCareHomeClick,
}) => {
  // Filter addresses to show only care home addresses
  const careHomeAddresses = addresses.filter(address => address.fromCareHome);

  return (
    <Paper sx={{ p: 3, height: "100%" }} elevation={elevation.paper}>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: validationErrors.addresses ? 1 : 2,
          }}
        >
          <Typography variant="h6">
            Shared Addresses
          </Typography>          
            <Tooltip title={careHomeAddresses.length > 0 ? "Change Care Home" : "Add Care Home"}>
              <IconButton onClick={onAddCareHome} size="small">
                {careHomeAddresses.length > 0 ? <EditIcon /> : <AddCircleOutlineIcon />}
              </IconButton>
            </Tooltip>          
        </Box>

        {validationErrors.addresses && (
          <Typography
            color="error"
            variant="caption"
            sx={{ display: "block" }}
          >
            {validationErrors.addresses}
          </Typography>
        )}
      </Box>

      {!!careHomeAddresses.length && (
        <Box>
          {careHomeAddresses.map((address, addressIndex) => {
            // Get the original index in the addresses array
            const originalIndex = addresses.findIndex(addr => 
              addr === address || (
                addr.street === address.street && 
                addr.postcode === address.postcode &&
                addr.nameNumber === address.nameNumber
              )
            );
            
            return (
              <Box
                key={originalIndex}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #e0e0e0",
                  borderRadius: borderRadius.large,
                  px: 2,
                  py: 0,
                  mt: 0.8,
                  mb: 0.8,
                  backgroundColor: address.isActive ? colors.background.sidebarGlass : colors.background.inactive,
                  opacity: address.isActive ? 1 : 0.6,
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: borderRadius.medium,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                    cursor: onCareHomeClick ? "pointer" : "default",
                  }}
                  onClick={onCareHomeClick}
                >
                  <Tooltip 
                    title={onCareHomeClick ? "Click to open care home details" : "Shared address"}
                    placement="top"
                  >
                    <HomeIcon
                      sx={{
                        fontSize: components.icon.large.width,
                        color: colors.status.warning,
                        width: 30,
                        height: 30,
                      }}
                    />
                  </Tooltip>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mr: 1 }} />

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: typography.fontWeight.medium,
                      fontSize: typography.fontSize.small,
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.3,
                    }}
                  >
                    {[
                      address.street,
                      address.nameNumber,
                      address.postcode,
                      address.town,
                      address.district,
                      address.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 0 }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={address.isActive}
                        disabled={disabled}
                        onChange={(e) =>
                          onUpdateAddress(originalIndex, {
                            isActive: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="caption">Active</Typography>
                    }
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        marginLeft: '4px'
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={address.isPrimary}
                        disabled={disabled}
                        onChange={(e) =>
                          onUpdateAddress(originalIndex, {
                            isPrimary: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="caption">Primary</Typography>
                    }
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        marginLeft: '4px'
                      }
                    }}
                  />

                  {!disabled && (
                    <>
                      <IconButton
                        onClick={() => onEditAddress(address)}
                        sx={{ p: 0.3, mr: 0.1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="delete"
                        onClick={() => onRemoveAddress(originalIndex)}
                        className="delete-button"
                        sx={{
                          "&:hover": { color: "error.main" },
                          p: 0.3,
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {careHomeAddresses.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 2,
            color: "text.secondary",
          }}
        >
          <HomeIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            No care home addresses
          </Typography>
          <Typography variant="caption">
            Click the + button to search and add a care home
          </Typography>
        </Box>
      )}
    </Paper>
  );
};